import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { getAuthHeaders } from '../utils/api';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const MODALITIES = ['Run', 'Bike', 'Erg', 'Row', 'Strength', 'Recovery', 'Other'];

const BACKFILL_DAY_OPTIONS = [7, 30, 90];

function formatDistance(meters) {
  if (!meters) return null;
  if (meters >= 1000) return `${(meters / 1000).toFixed(1)} km`;
  return `${meters} m`;
}

function formatSyncSummary(data) {
  if (data.fetched === 0 && data.emptyMessage) {
    return data.emptyMessage;
  }
  const parts = [
    `${data.synced || 0} imported`,
    `${data.pendingModality || 0} need activity type`
  ];
  if (data.possibleDuplicates) {
    parts.push(`${data.possibleDuplicates} possible duplicate${data.possibleDuplicates === 1 ? '' : 's'} held for review`);
  }
  parts.push(`${data.skipped || 0} skipped`);
  return `${parts.join(', ')}.`;
}

function networkErrorMessage(err, fallback) {
  const msg = err?.message || '';
  if (msg === 'Failed to fetch' || err?.name === 'TypeError') {
    return 'Could not reach the server. If you were importing, wait a minute and refresh your training log — it may still finish.';
  }
  return msg || fallback;
}

export default function StravaIntegration() {
  const { token } = useAuth();
  const [status, setStatus] = useState(null);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [selectedModality, setSelectedModality] = useState({});
  const [completingId, setCompletingId] = useState(null);
  const [dismissingId, setDismissingId] = useState(null);
  const [showHistoryImport, setShowHistoryImport] = useState(false);
  const [historyDays, setHistoryDays] = useState(30);
  const [syncEnabled, setSyncEnabled] = useState(true);
  const [rowingDefault, setRowingDefault] = useState('Row');
  const [defaultBoatId, setDefaultBoatId] = useState('');
  const [defaultZoneKey, setDefaultZoneKey] = useState('');
  const [zoneDefinitions, setZoneDefinitions] = useState([]);
  const [zoneLoggingEnabled, setZoneLoggingEnabled] = useState(false);
  const [boats, setBoats] = useState([]);
  const [savingSettings, setSavingSettings] = useState(false);

  const loadBoats = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/strava/boats`, {
        headers: getAuthHeaders(token)
      });
      if (!res.ok) return;
      const data = await res.json();
      setBoats(data.boats || []);
    } catch {
      setBoats([]);
    }
  }, [token]);

  const loadPending = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/strava/pending`, {
        headers: getAuthHeaders(token)
      });
      if (!res.ok) return;
      const data = await res.json();
      setPending(data.pending || []);
    } catch {
      setPending([]);
    }
  }, [token]);

  const loadStatus = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/strava/status`, {
        headers: getAuthHeaders(token)
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to load Strava status');
      }
      setStatus(data);
      if (data.connected) {
        setSyncEnabled(data.syncEnabled !== false);
        setRowingDefault(data.rowingDefaultModality === 'Erg' ? 'Erg' : 'Row');
        setDefaultBoatId(data.defaultBoatId ? String(data.defaultBoatId) : '');
        setZoneLoggingEnabled(Boolean(data.zoneLoggingEnabled));
        const defs = Array.isArray(data.zoneDefinitions) ? data.zoneDefinitions : [];
        setZoneDefinitions(defs);
        setDefaultZoneKey(data.defaultZoneKey || defs[0]?.key || '');
        await Promise.all([loadPending(), loadBoats()]);
      } else {
        setPending([]);
      }
    } catch (err) {
      setStatus({ enabled: true, connected: false });
      setPending([]);
      setMessage(networkErrorMessage(err, 'Failed to load Strava status'));
    } finally {
      setLoading(false);
    }
  }, [token, loadPending, loadBoats]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    let strava = params.get('strava');
    if (!strava) {
      try {
        strava = sessionStorage.getItem('stravaOAuthResult');
      } catch {
        strava = null;
      }
    }
    if (strava === 'connected') {
      setMessage('Strava connected. Use Sync new activities for workouts since you connected. Import history only if you want past Strava data.');
      loadStatus();
      params.delete('strava');
      params.delete('message');
      const next = `${window.location.pathname}${params.toString() ? `?${params}` : ''}`;
      window.history.replaceState({}, '', next);
      try {
        sessionStorage.removeItem('stravaOAuthResult');
      } catch {
        // private mode
      }
    } else if (strava === 'error') {
      const reason = params.get('message');
      setMessage(
        reason
          ? `Could not connect Strava (${reason}). Check that STRAVA_REDIRECT_URI on the server is https://api.rowcentral.club/api/strava/callback and that Strava's Authorization Callback Domain is api.rowcentral.club.`
          : 'Could not connect Strava. Check server Strava settings and try again.'
      );
      params.delete('strava');
      params.delete('message');
      const next = `${window.location.pathname}${params.toString() ? `?${params}` : ''}`;
      window.history.replaceState({}, '', next);
      try {
        sessionStorage.removeItem('stravaOAuthResult');
      } catch {
        // private mode
      }
    }
  }, [loadStatus]);

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    setMessage('');
    try {
      const res = await fetch(`${API_URL}/api/strava/settings`, {
        method: 'PATCH',
        headers: { ...getAuthHeaders(token), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          syncEnabled,
          rowingDefaultModality: rowingDefault,
          defaultBoatId: defaultBoatId ? parseInt(defaultBoatId, 10) : null,
          defaultZoneKey: defaultZoneKey || null
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save settings');
      setMessage('Strava settings saved.');
      await loadStatus();
    } catch (err) {
      setMessage(networkErrorMessage(err, 'Failed to save settings'));
    } finally {
      setSavingSettings(false);
    }
  };

  const handleConnect = async () => {
    setBusy(true);
    setMessage('');
    try {
      const res = await fetch(
        `${API_URL}/api/strava/connect-url?returnTo=${encodeURIComponent(window.location.origin)}`,
        {
          headers: getAuthHeaders(token)
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to start Strava connect');
      window.location.href = data.authorizeUrl;
    } catch (err) {
      setMessage(networkErrorMessage(err, 'Failed to connect Strava'));
      setBusy(false);
    }
  };

  const handleDisconnect = async () => {
    setBusy(true);
    setMessage('');
    try {
      const res = await fetch(`${API_URL}/api/strava/disconnect`, {
        method: 'DELETE',
        headers: getAuthHeaders(token)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Disconnect failed');
      setMessage('Strava disconnected.');
      await loadStatus();
    } catch (err) {
      setMessage(networkErrorMessage(err, 'Failed to disconnect'));
    } finally {
      setBusy(false);
    }
  };

  const runSync = async (body) => {
    const res = await fetch(`${API_URL}/api/strava/sync-now`, {
      method: 'POST',
      headers: { ...getAuthHeaders(token), 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    const data = await res.json();
    if (!res.ok) {
      const err = new Error(data.error || 'Sync failed');
      err.hint = data.hint;
      err.details = data;
      throw err;
    }
    return data;
  };

  const runSyncUntilDone = async (body, onProgress) => {
    const totals = { synced: 0, pendingModality: 0, possibleDuplicates: 0 };
    let last = null;
    for (let round = 0; round < 15; round += 1) {
      last = await runSync(body);
      totals.synced += last.synced || 0;
      totals.pendingModality += last.pendingModality || 0;
      totals.possibleDuplicates += last.possibleDuplicates || 0;
      if (!last.incomplete) {
        return { ...last, ...totals };
      }
      if (onProgress) {
        onProgress(`Imported ${totals.synced} so far. Still working…`);
      }
    }
    return {
      ...last,
      ...totals,
      emptyMessage: `Imported ${totals.synced} so far, but the import is still running. Wait a minute and refresh your training log.`
    };
  };

  const handleSyncNew = async () => {
    setBusy(true);
    setMessage('');
    setShowHistoryImport(false);
    try {
      const data = await runSyncUntilDone({ mode: 'incremental' }, setMessage);
      setMessage(data.emptyMessage || `Sync complete: ${formatSyncSummary(data)}`);
      await loadStatus();
    } catch (err) {
      const hint = err.hint ? ` ${err.hint}` : '';
      setMessage(networkErrorMessage(err, 'Sync failed') + hint);
    } finally {
      setBusy(false);
    }
  };

  const handleImportHistory = async () => {
    const confirmed = window.confirm(
      `Import Strava activities from the last ${historyDays} days?\n\n` +
      'This may duplicate workouts you already logged manually in Central. ' +
      'Only use this if you want past Strava data imported.'
    );
    if (!confirmed) return;

    setBusy(true);
    setMessage('');
    try {
      const data = await runSyncUntilDone(
        { mode: 'backfill', days: historyDays },
        setMessage
      );
      setMessage(data.emptyMessage || `History import complete (${historyDays} days): ${formatSyncSummary(data)}`);
      setShowHistoryImport(false);
      await loadStatus();
    } catch (err) {
      const hint = err.hint ? ` ${err.hint}` : '';
      setMessage(networkErrorMessage(err, 'Import failed') + hint);
    } finally {
      setBusy(false);
    }
  };

  const handleDismiss = async (pendingId) => {
    setDismissingId(pendingId);
    setMessage('');
    try {
      const res = await fetch(`${API_URL}/api/strava/pending/${pendingId}/dismiss`, {
        method: 'POST',
        headers: getAuthHeaders(token)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Dismiss failed');
      setMessage('Activity dismissed. It will not be imported.');
      await loadStatus();
    } catch (err) {
      setMessage(err.message || 'Dismiss failed');
    } finally {
      setDismissingId(null);
    }
  };

  const handleComplete = async (pendingId, fallbackModality) => {
    const modality = selectedModality[pendingId] || fallbackModality;
    if (!modality) {
      setMessage('Select an activity type before importing.');
      return;
    }
    setCompletingId(pendingId);
    setMessage('');
    try {
      const res = await fetch(`${API_URL}/api/strava/pending/${pendingId}/complete`, {
        method: 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify({ modality })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Import failed');
      setMessage('Activity imported to your training log.');
      setSelectedModality((prev) => {
        const next = { ...prev };
        delete next[pendingId];
        return next;
      });
      await loadStatus();
    } catch (err) {
      setMessage(err.message || 'Import failed');
    } finally {
      setCompletingId(null);
    }
  };

  if (loading) {
    return (
      <div className="text-sm text-gray-500">Loading Strava status…</div>
    );
  }

  if (!status?.enabled) {
    return (
      <div className="text-sm text-gray-500">
        Strava sync is not configured on this server yet.
      </div>
    );
  }

  const isConnected = Boolean(status.connected);

  return (
    <div className="space-y-3">
      <div
        className={`rounded-lg border px-4 py-3 ${
          isConnected
            ? 'border-green-200 bg-green-50'
            : 'border-gray-200 bg-gray-50'
        }`}
      >
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <span
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${
              isConnected
                ? 'bg-green-100 text-green-800'
                : 'bg-gray-200 text-gray-700'
            }`}
          >
            {isConnected ? (
              <>
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
                Connection active
              </>
            ) : (
              'Not connected'
            )}
          </span>
          {isConnected && status.syncEnabled === false && (
            <span className="text-xs text-amber-700">Sync paused</span>
          )}
        </div>
        <p className={`text-sm ${isConnected ? 'text-green-900' : 'text-gray-600'}`}>
          {isConnected
            ? 'Your Strava account is linked. Past Strava history is not imported unless you choose Import history.'
            : 'Connect your Strava account to import runs, rides, rows, and other workouts into Central.'}
        </p>
        {!isConnected && status.redirectUri && !/\/api\/strava\/callback$/.test(status.redirectUri) && (
          <p className="text-xs text-amber-800 mt-2">
            Server callback is <code className="bg-amber-100 px-1 rounded">{status.redirectUri}</code>.
            That should be the API, e.g. <code className="bg-amber-100 px-1 rounded">https://api.rowcentral.club/api/strava/callback</code>.
          </p>
        )}
        {!isConnected && status.lastConnectError?.message && (
          <p className="text-xs text-red-700 mt-2">
            Last connect error: {status.lastConnectError.message}
          </p>
        )}
        {isConnected && (
          <p className="text-xs text-green-800/80 mt-2">
            {status.stravaAthleteName ? `Connected as ${status.stravaAthleteName}` : 'Connection active'}
            {status.lastSyncAt ? ` · Last sync: ${new Date(status.lastSyncAt).toLocaleString()}` : ' · Not synced yet'}
            {(status.pendingModalityCount || 0) > 0
              ? ` · ${status.pendingModalityCount} held for review`
              : ''}
          </p>
        )}
        {isConnected && status.stravaAppInactive && (
          <p className="text-xs mt-1 text-red-800">
            Strava is blocking this app until the Strava account that owns it has an active subscription.
          </p>
        )}
        {isConnected && !status.stravaAppInactive && (
          <p className="text-xs mt-1 text-amber-800">
            {status.webhookConfigured
              ? 'New Strava workouts are imported automatically.'
              : 'New Strava workouts are imported when you tap Sync new activities.'}
          </p>
        )}
      </div>

      {isConnected && pending.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 space-y-3">
          <h3 className="text-sm font-semibold text-amber-900">
            Needs your review ({pending.length})
          </h3>
          <p className="text-xs text-amber-800">
            These Strava activities are held before appearing in your training log — some need an activity type, others look like duplicates of workouts you already logged.
          </p>
          {pending.map((activity) => {
            const distance = formatDistance(activity.distanceMeters);
            const isDuplicate = activity.status === 'possible_duplicate';
            const modalityValue = selectedModality[activity.id] || activity.mappedModality || '';
            return (
              <div key={activity.id} className="rounded-lg border border-amber-100 bg-white p-3 space-y-2">
                <div>
                  <p className="font-medium text-gray-900">{activity.title}</p>
                  <p className="text-xs text-gray-600 mt-1">
                    {[activity.logDate, activity.stravaSportType, activity.minutes ? `${activity.minutes} min` : null, distance]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                  {isDuplicate && (
                    <p className="text-xs text-red-700 mt-1">
                      {activity.duplicateReason || 'Looks like a duplicate of a workout you already logged.'} Import only if it is a different session.
                    </p>
                  )}
                </div>
                <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                  <select
                    value={modalityValue}
                    onChange={(e) =>
                      setSelectedModality((prev) => ({ ...prev, [activity.id]: e.target.value }))
                    }
                    className="border rounded px-3 py-2 text-sm bg-white text-gray-900 flex-1"
                  >
                    <option value="">Select activity type…</option>
                    {MODALITIES.map((mod) => (
                      <option key={mod} value={mod}>{mod}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => handleComplete(activity.id, activity.mappedModality)}
                    disabled={completingId === activity.id || dismissingId === activity.id || !modalityValue}
                    className="btn-theme-primary px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
                  >
                    {completingId === activity.id ? 'Importing…' : isDuplicate ? 'Import anyway' : 'Import to training log'}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDismiss(activity.id)}
                    disabled={completingId === activity.id || dismissingId === activity.id}
                    className="px-4 py-2 rounded-lg text-sm font-semibold border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    {dismissingId === activity.id ? 'Dismissing…' : isDuplicate ? "Don't import" : 'Dismiss'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {isConnected && (
        <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-3">
          <h3 className="text-sm font-semibold text-gray-900">Strava settings</h3>
          <label className="flex items-center gap-2 text-sm text-gray-800">
            <input
              type="checkbox"
              checked={syncEnabled}
              onChange={(e) => setSyncEnabled(e.target.checked)}
            />
            Automatic sync (webhooks + manual sync when enabled)
          </label>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 text-sm text-gray-800">
            <span>When Strava says &quot;Rowing&quot;, import as:</span>
            <select
              value={rowingDefault}
              onChange={(e) => setRowingDefault(e.target.value)}
              className="border rounded px-3 py-2 text-sm bg-white text-gray-900 w-fit"
            >
              <option value="Row">Row (on-water)</option>
              <option value="Erg">Erg (indoor)</option>
            </select>
          </div>
          <div className="flex flex-col gap-1 text-sm text-gray-800">
            <label className="flex flex-col sm:flex-row sm:items-center gap-2">
              <span>Default boat for rows without a reservation:</span>
              <select
                value={defaultBoatId}
                onChange={(e) => setDefaultBoatId(e.target.value)}
                className="border rounded px-3 py-2 text-sm bg-white text-gray-900 w-fit"
              >
                <option value="">None — ask me each time</option>
                {boats.map((boat) => (
                  <option key={boat.id} value={boat.id}>
                    {boat.name}{boat.boatClass ? ` (${boat.boatClass})` : ''}
                  </option>
                ))}
              </select>
            </label>
            <p className="text-xs text-gray-500">
              When a Strava row overlaps a boat reservation, those meters always go to the reserved boat.
              This is the fallback for rows with no reservation. Erg pieces are never auto-assigned to a boat.
            </p>
          </div>
          {zoneLoggingEnabled && zoneDefinitions.length > 0 && (
            <div className="flex flex-col gap-1 text-sm text-gray-800">
              <label className="flex flex-col sm:flex-row sm:items-center gap-2">
                <span>Default training zone for imports:</span>
                <select
                  value={
                    zoneDefinitions.some((zone) => zone.key === defaultZoneKey)
                      ? defaultZoneKey
                      : (zoneDefinitions[0]?.key || '')
                  }
                  onChange={(e) => setDefaultZoneKey(e.target.value)}
                  className="border rounded px-3 py-2 text-sm bg-white text-gray-900 w-fit"
                >
                  {zoneDefinitions.map((zone) => (
                    <option key={zone.key} value={zone.key}>
                      {zone.label || zone.key}
                    </option>
                  ))}
                </select>
              </label>
              <p className="text-xs text-gray-500">
                Strava duration is saved as this zone's minutes. Teams that do not use zone logging still use total minutes.
              </p>
            </div>
          )}
          <button
            type="button"
            onClick={handleSaveSettings}
            disabled={savingSettings || busy}
            className="px-4 py-2 rounded-lg text-sm font-semibold border border-gray-300 text-gray-800 hover:bg-gray-50 disabled:opacity-50"
          >
            {savingSettings ? 'Saving…' : 'Save settings'}
          </button>
        </div>
      )}

      {message && (
        <p className="text-sm text-blue-800 bg-blue-50 border border-blue-200 rounded px-3 py-2">{message}</p>
      )}

      {isConnected && showHistoryImport && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 space-y-3">
          <h3 className="text-sm font-semibold text-amber-900">Import Strava history</h3>
          <p className="text-xs text-amber-800">
            May duplicate workouts you already logged in Central. Use only if you want past Strava activities imported.
          </p>
          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <label className="text-sm text-amber-900 flex items-center gap-2">
              <span>Period:</span>
              <select
                value={historyDays}
                onChange={(e) => setHistoryDays(parseInt(e.target.value, 10))}
                className="border rounded px-3 py-2 text-sm bg-white text-gray-900"
              >
                {BACKFILL_DAY_OPTIONS.map((d) => (
                  <option key={d} value={d}>Last {d} days</option>
                ))}
              </select>
            </label>
            <button
              type="button"
              onClick={handleImportHistory}
              disabled={busy}
              className="px-4 py-2 rounded-lg text-sm font-semibold bg-amber-700 text-white hover:bg-amber-800 disabled:opacity-50"
            >
              {busy ? 'Importing…' : 'Confirm import'}
            </button>
            <button
              type="button"
              onClick={() => setShowHistoryImport(false)}
              disabled={busy}
              className="px-4 py-2 rounded-lg text-sm font-semibold border border-amber-300 text-amber-900"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {!isConnected ? (
          <button
            type="button"
            onClick={handleConnect}
            disabled={busy}
            className="btn-theme-primary px-4 py-2 rounded-lg text-sm font-semibold"
          >
            Connect with Strava
          </button>
        ) : (
          <>
            <button
              type="button"
              onClick={handleSyncNew}
              disabled={busy}
              className="btn-theme-primary px-4 py-2 rounded-lg text-sm font-semibold"
            >
              {busy && !showHistoryImport ? 'Syncing…' : 'Sync new activities'}
            </button>
            <button
              type="button"
              onClick={() => setShowHistoryImport((v) => !v)}
              disabled={busy}
              className="px-4 py-2 rounded-lg text-sm font-semibold border border-amber-400 text-amber-900 bg-amber-50 hover:bg-amber-100"
            >
              Import history…
            </button>
            <button
              type="button"
              onClick={handleDisconnect}
              disabled={busy}
              className="px-4 py-2 rounded-lg text-sm font-semibold border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Disconnect
            </button>
          </>
        )}
      </div>
    </div>
  );
}
